import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, MessageCircle, RefreshCw, FileText, CheckCircle2, Save, Loader2, List, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
const SERVICE_TYPES = ['Menú Digital Fase 1', 'Menú Digital Fase 2', 'Menú Digital Completo', 'Landing Page', 'KDS Sistema Cocina', 'Delivery System', 'Personalizado'];
const PAYMENT_METHODS = ['SINPE Móvil', 'Transferencia Bancaria', 'Efectivo', 'Otro'];
const PAYMENT_TYPES = ['Pago Total', 'Primer Abono (50%)', 'Segundo Abono (50%)', 'Abono Parcial'];
const DEFAULT_DESCRIPTIONS: Record<string, string> = {
  'Menú Digital Fase 1': 'Implementación de Menú Digital Fase 1. Incluye configuración inicial, carga de menú, y código QR.',
  'Menú Digital Fase 2': 'Implementación de Menú Digital Fase 2. Incluye sistema de pedidos, integración con KDS y reportes básicos.',
  'Menú Digital Completo': 'Sistema integral de Menú Digital. Incluye pedidos, KDS, reportes avanzados y módulo de lealtad.',
  'Landing Page': 'Diseño y desarrollo de Landing Page personalizada con integración al menú digital.',
  'KDS Sistema Cocina': 'Implementación de Kitchen Display System (KDS) para gestión de comandas en cocina.',
  'Delivery System': 'Módulo completo de delivery con asignación de choferes y tracking de órdenes.',
  Personalizado: 'Servicios de desarrollo y configuración personalizados.'
};
// ─── Line items + discount: encoded into service_description ─────────
// We persist the extra data (lineItems[], discount) as a JSON marker
// appended to service_description so we don't need a DB migration and
// existing saved receipts stay 100% backwards compatible:
//   <plain text description>
//   <!--__DETAILS__:{"items":[...],"discount":1234}-->
// On load, we split on the marker; the plain text stays in the
// description textarea, the JSON repopulates the structured fields.
const DETAILS_MARKER = '<!--__DETAILS__:';
const DETAILS_END = '-->';
type LineItem = {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
};
const newLineItem = (): LineItem => ({
  id: Math.random().toString(36).slice(2, 10),
  description: '',
  quantity: 1,
  unitPrice: 0
});
const encodeDescription = (plain: string, items: LineItem[], discount: number): string => {
  if (items.length === 0 && (!discount || discount === 0)) return plain;
  const payload = JSON.stringify({
    items: items.map(it => ({
      description: it.description,
      quantity: it.quantity,
      unitPrice: it.unitPrice
    })),
    discount: discount || 0
  });
  return `${plain}\n\n${DETAILS_MARKER}${payload}${DETAILS_END}`;
};
const decodeDescription = (raw: string): {
  plain: string;
  items: LineItem[];
  discount: number;
} => {
  if (!raw || !raw.includes(DETAILS_MARKER)) {
    return {
      plain: raw || '',
      items: [],
      discount: 0
    };
  }
  try {
    const idx = raw.indexOf(DETAILS_MARKER);
    const plain = raw.slice(0, idx).trimEnd();
    const after = raw.slice(idx + DETAILS_MARKER.length);
    const endIdx = after.indexOf(DETAILS_END);
    const json = endIdx >= 0 ? after.slice(0, endIdx) : after;
    const parsed = JSON.parse(json);
    const items: LineItem[] = (parsed.items || []).map((it: any) => ({
      id: Math.random().toString(36).slice(2, 10),
      description: String(it.description || ''),
      quantity: Number(it.quantity) || 0,
      unitPrice: Number(it.unitPrice) || 0
    }));
    return {
      plain,
      items,
      discount: Number(parsed.discount) || 0
    };
  } catch {
    return {
      plain: raw,
      items: [],
      discount: 0
    };
  }
};
export function ReceiptGeneratorPage() {
  const navigate = useNavigate();
  const getNextReceiptNumber = async (): Promise<string> => {
    const year = new Date().getFullYear();
    const prefix = `NOIZA-${year}-`;
    try {
      const {
        data
      } = await supabase.from('noiza_receipts').select('receipt_number').like('receipt_number', `${prefix}%`).order('receipt_number', {
        ascending: false
      }).limit(1);
      if (data && data.length > 0) {
        const lastNum = parseInt(data[0].receipt_number.replace(prefix, ''), 10);
        return `${prefix}${String((lastNum || 0) + 1).padStart(4, '0')}`;
      }
      return `${prefix}0001`;
    } catch {
      return `${prefix}0001`;
    }
  };
  const [formData, setFormData] = useState({
    receiptNumber: '',
    date: new Date().toISOString().split('T')[0],
    clientName: '',
    clientId: '',
    clientPhone: '',
    serviceType: 'Menú Digital Fase 1',
    serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
    totalAmount: 100000,
    paymentAmount: 50000,
    paymentMethod: 'SINPE Móvil',
    paymentReference: '',
    paymentType: 'Primer Abono (50%)',
    notes: ''
  });
  // Line items and discount (new — optional desglose)
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [discount, setDiscount] = useState<number>(0);
  const [savedReceipts, setSavedReceipts] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  // Derived totals — when there are line items, the total is computed
  // from them minus the discount; the user's manually-entered
  // totalAmount becomes informational only and is auto-synced.
  const lineItemsSubtotal = lineItems.reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0);
  const hasLineItems = lineItems.length > 0;
  const effectiveTotal = hasLineItems ? Math.max(0, lineItemsSubtotal - (Number(discount) || 0)) : formData.totalAmount;
  const remainingBalance = effectiveTotal - formData.paymentAmount;
  // Keep formData.totalAmount in sync with the computed total whenever
  // line items or discount change, so the existing flows (payment %,
  // saved totalAmount on the DB, etc.) keep working unchanged.
  useEffect(() => {
    if (hasLineItems) {
      setFormData(prev => {
        if (prev.totalAmount === effectiveTotal) return prev;
        return {
          ...prev,
          totalAmount: effectiveTotal
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemsSubtotal, discount, hasLineItems]);
  useEffect(() => {
    fetchReceipts();
    // Set initial sequential receipt number
    getNextReceiptNumber().then(num => {
      setFormData(prev => ({
        ...prev,
        receiptNumber: num
      }));
    });
  }, []);
  const fetchReceipts = async () => {
    setLoadingReceipts(true);
    try {
      const {
        data,
        error
      } = await supabase.from('noiza_receipts').select('*').order('created_at', {
        ascending: false
      }).limit(20);
      if (error) throw error;
      if (data) setSavedReceipts(data);
    } catch (err) {
      console.error('Error fetching receipts:', err);
    } finally {
      setLoadingReceipts(false);
    }
  };
  const handleSave = async () => {
    if (!formData.clientName) {
      alert('Por favor ingresa el nombre del cliente');
      return;
    }
    setSaving(true);
    try {
      // Check if receipt number already exists
      const {
        data: existing
      } = await supabase.from('noiza_receipts').select('id').eq('receipt_number', formData.receiptNumber).single();
      const payload = {
        receipt_number: formData.receiptNumber,
        receipt_date: formData.date,
        client_name: formData.clientName,
        client_id: formData.clientId,
        client_phone: formData.clientPhone,
        service_type: formData.serviceType,
        // Encode line items + discount into service_description so the
        // existing DB schema stays untouched.
        service_description: encodeDescription(formData.serviceDescription, lineItems, discount),
        total_amount: effectiveTotal,
        payment_amount: formData.paymentAmount,
        remaining_balance: remainingBalance,
        payment_method: formData.paymentMethod,
        payment_reference: formData.paymentReference,
        payment_type: formData.paymentType,
        notes: formData.notes
      };
      let error;
      if (existing) {
        // Update
        const {
          error: updateError
        } = await supabase.from('noiza_receipts').update(payload).eq('id', existing.id);
        error = updateError;
      } else {
        // Insert
        const {
          error: insertError
        } = await supabase.from('noiza_receipts').insert([payload]);
        error = insertError;
      }
      if (error) throw error;
      setSaveSuccess(true);
      fetchReceipts();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving receipt:', err);
      alert('Error al guardar el recibo. Revisa la consola para más detalles.');
    } finally {
      setSaving(false);
    }
  };
  const loadReceipt = (receipt: any) => {
    const decoded = decodeDescription(receipt.service_description || '');
    setFormData({
      receiptNumber: receipt.receipt_number,
      date: receipt.receipt_date,
      clientName: receipt.client_name,
      clientId: receipt.client_id || '',
      clientPhone: receipt.client_phone || '',
      serviceType: receipt.service_type,
      serviceDescription: decoded.plain,
      totalAmount: receipt.total_amount,
      paymentAmount: receipt.payment_amount,
      paymentMethod: receipt.payment_method,
      paymentReference: receipt.payment_reference || '',
      paymentType: receipt.payment_type,
      notes: receipt.notes || ''
    });
    setLineItems(decoded.items);
    setDiscount(decoded.discount);
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      serviceType: newType,
      serviceDescription: DEFAULT_DESCRIPTIONS[newType] || ''
    }));
  };
  const handleReset = async () => {
    const nextNum = await getNextReceiptNumber();
    setFormData({
      receiptNumber: nextNum,
      date: new Date().toISOString().split('T')[0],
      clientName: '',
      clientId: '',
      clientPhone: '',
      serviceType: 'Menú Digital Fase 1',
      serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
      totalAmount: 100000,
      paymentAmount: 50000,
      paymentMethod: 'SINPE Móvil',
      paymentReference: '',
      paymentType: 'Primer Abono (50%)',
      notes: ''
    });
    setLineItems([]);
    setDiscount(0);
  };
  const handlePrint = () => {
    window.print();
  };
  const handleWhatsAppShare = () => {
    const text = `*RECIBO DE PAGO - NOIZA* 🧾

*Recibo N°:* ${formData.receiptNumber}
*Fecha:* ${formData.date}
*Cliente:* ${formData.clientName}

*Servicio:* ${formData.serviceType}
*Tipo de Pago:* ${formData.paymentType}
*Método:* ${formData.paymentMethod} ${formData.paymentReference ? `(Ref: ${formData.paymentReference})` : ''}

*Monto Pagado:* ${formatCurrency(formData.paymentAmount)}
*Saldo Pendiente:* ${formatCurrency(remainingBalance)}

Gracias por confiar en NOIZA.
Plataforma de Delivery Digital - Costa Rica
noizacr.com`;
    const encodedText = encodeURIComponent(text);
    let phone = formData.clientPhone.replace(/\D/g, '');
    if (phone && !phone.startsWith('506')) {
      phone = '506' + phone;
    }
    const url = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
    window.open(url, '_blank');
  };
  // Line item handlers
  const addLineItem = () => setLineItems(prev => [...prev, newLineItem()]);
  const updateLineItem = (id: string, field: keyof Omit<LineItem, 'id'>, value: string | number) => setLineItems(prev => prev.map(it => it.id === id ? {
    ...it,
    [field]: field === 'description' ? String(value) : Number(value) || 0
  } : it));
  const removeLineItem = (id: string) => setLineItems(prev => prev.filter(it => it.id !== id));
  return <div className="min-h-screen bg-[#0B0B0B] text-white print:bg-white print:text-black" data-id="element-5751">
      {/* Print Styles */}
      <style data-id="element-5752">
        {`
          @media print {
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .receipt-container {
              box-shadow: none !important;
              border: none !important;
              padding: 24px !important;
              margin: 0 auto !important;
              width: 100% !important;
              max-width: 100% !important;
              border-radius: 0 !important;
              background: white !important;
              color: black !important;
            }
            .print-text-black { color: black !important; }
            .print-border-black { border-color: #e5e7eb !important; }
            .print-bg-gray { background-color: #f9fafb !important; }
          }
        `}
      </style>

      {/* Header - Hidden in print */}
      <div className="no-print sticky top-0 z-10 bg-[#0B0B0B]/95 backdrop-blur-md border-b border-white/10 px-4 sm:px-6 py-4" data-id="element-5753">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-5754">
          <div className="flex items-center gap-4" data-id="element-5755">
            <button onClick={() => navigate('/admin/enrollment')} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-id="element-5756">
              <ArrowLeft className="w-5 h-5 text-white/70" data-id="element-5757" />
            </button>
            <div data-id="element-5758">
              <h1 className="text-xl font-bold text-white" data-id="element-5759">
                Generador de Recibos
              </h1>
              <p className="text-sm text-white/50" data-id="element-5760">NOIZA Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-5761">
            <button onClick={handleReset} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-5762">
              <RefreshCw className="w-4 h-4" data-id="element-5763" />
              <span className="hidden sm:inline" data-id="element-5764">Nuevo</span>
            </button>

            <button onClick={handleSave} disabled={saving} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${saveSuccess ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`} data-id="element-5765">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-5766" /> : saveSuccess ? <CheckCircle2 className="w-4 h-4" data-id="element-5767" /> : <Save className="w-4 h-4" data-id="element-5768" />}
              <span className="hidden sm:inline" data-id="element-5769">
                {saving ? 'Guardando...' : saveSuccess ? 'Guardado' : 'Guardar'}
              </span>
            </button>

            <button onClick={handleWhatsAppShare} className="px-4 py-2 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-5770">
              <MessageCircle className="w-4 h-4" data-id="element-5771" />
              <span className="hidden sm:inline" data-id="element-5772">WhatsApp</span>
            </button>
            <button onClick={handlePrint} className="px-4 py-2 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#C9A34E]/90 rounded-lg text-sm font-bold transition-colors flex items-center gap-2" data-id="element-5773">
              <Printer className="w-4 h-4" data-id="element-5774" />
              <span className="hidden sm:inline" data-id="element-5775">Imprimir / PDF</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-5776">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-5777">
          {/* Form Section */}
          <div className="lg:col-span-5 space-y-6 no-print" data-id="element-5778">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6" data-id="element-5779">
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-5780">
                <FileText className="w-5 h-5 text-[#C9A34E]" data-id="element-5781" />
                Datos del Recibo
              </h2>

              <div className="space-y-5" data-id="element-5782">
                <div className="grid grid-cols-2 gap-4" data-id="element-5783">
                  <div data-id="element-5784">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5785">
                      N° de Recibo
                    </label>
                    <input type="text" value={formData.receiptNumber} onChange={e => setFormData({
                    ...formData,
                    receiptNumber: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5786" />
                  </div>
                  <div data-id="element-5787">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5788">
                      Fecha
                    </label>
                    <input type="date" value={formData.date} onChange={e => setFormData({
                    ...formData,
                    date: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5789" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-5790">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-5791">
                    Cliente
                  </h3>
                  <div data-id="element-5792">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5793">
                      Nombre / Razón Social
                    </label>
                    <input type="text" value={formData.clientName} onChange={e => setFormData({
                    ...formData,
                    clientName: e.target.value
                  })} placeholder="Ej. Restaurante El Sabor" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5794" />
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-5795">
                    <div data-id="element-5796">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5797">
                        Cédula (Opcional)
                      </label>
                      <input type="text" value={formData.clientId} onChange={e => setFormData({
                      ...formData,
                      clientId: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5798" />
                    </div>
                    <div data-id="element-5799">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5800">
                        Teléfono / WhatsApp
                      </label>
                      <input type="text" value={formData.clientPhone} onChange={e => setFormData({
                      ...formData,
                      clientPhone: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5801" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-5802">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-5803">
                    Servicio
                  </h3>
                  <div data-id="element-5804">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5805">
                      Tipo de Servicio
                    </label>
                    <select value={formData.serviceType} onChange={handleServiceTypeChange} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5806">
                      {SERVICE_TYPES.map(type => <option key={type} value={type} data-id="element-5807">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-5808">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5809">
                      Descripción
                    </label>
                    <textarea value={formData.serviceDescription} onChange={e => setFormData({
                    ...formData,
                    serviceDescription: e.target.value
                  })} rows={3} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-5810" />
                  </div>
                </div>

                {/* ─── Desglose (Line Items) ───────────────────── */}
                <div className="space-y-3" data-id="element-5811">
                  <div className="flex items-center justify-between border-b border-white/10 pb-2" data-id="element-5812">
                    <h3 className="text-sm font-semibold text-white/80" data-id="element-5813">
                      Desglose del Cobro{' '}
                      <span className="text-xs text-white/40 font-normal" data-id="element-5814">
                        (Opcional)
                      </span>
                    </h3>
                    <button type="button" onClick={addLineItem} className="text-xs font-medium text-[#C9A34E] hover:text-[#C9A34E]/80 flex items-center gap-1" data-id="element-5815">
                      <Plus className="w-3.5 h-3.5" data-id="element-5816" />
                      Agregar línea
                    </button>
                  </div>

                  {lineItems.length === 0 ? <p className="text-xs text-white/40 italic" data-id="element-5817">
                      Sin desglose. El recibo usará el monto total único de
                      arriba. Agregá líneas para desglosar por
                      producto/servicio.
                    </p> : <div className="space-y-2" data-id="element-5818">
                      {lineItems.map((it, idx) => {
                    const subtotal = (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
                    return <div key={it.id} className="bg-[#0B0B0B] border border-white/10 rounded-lg p-3 space-y-2" data-id="element-5819">
                            <div className="flex items-center justify-between" data-id="element-5820">
                              <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold" data-id="element-5821">
                                Línea {idx + 1}
                              </span>
                              <button type="button" onClick={() => removeLineItem(it.id)} className="text-white/30 hover:text-red-400 transition-colors" aria-label="Eliminar línea" data-id="element-5822">
                                <Trash2 className="w-3.5 h-3.5" data-id="element-5823" />
                              </button>
                            </div>
                            <input type="text" value={it.description} onChange={e => updateLineItem(it.id, 'description', e.target.value)} placeholder="Descripción del producto/servicio" className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5824" />
                            <div className="grid grid-cols-3 gap-2" data-id="element-5825">
                              <div data-id="element-5826">
                                <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-5827">
                                  Cantidad
                                </label>
                                <input type="number" min={0} step={1} value={it.quantity} onChange={e => updateLineItem(it.id, 'quantity', e.target.value)} className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5828" />
                              </div>
                              <div data-id="element-5829">
                                <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-5830">
                                  Precio Unit. ₡
                                </label>
                                <input type="number" min={0} value={it.unitPrice} onChange={e => updateLineItem(it.id, 'unitPrice', e.target.value)} className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5831" />
                              </div>
                              <div data-id="element-5832">
                                <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-5833">
                                  Subtotal
                                </label>
                                <div className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-[#C9A34E] text-sm font-bold" data-id="element-5834">
                                  {formatCurrency(subtotal)}
                                </div>
                              </div>
                            </div>
                          </div>;
                  })}

                      {/* Subtotal + Discount summary inside form */}
                      <div className="bg-[#0B0B0B] border border-white/10 rounded-lg p-3 space-y-2" data-id="element-5835">
                        <div className="flex justify-between text-xs text-white/60" data-id="element-5836">
                          <span data-id="element-5837">Subtotal líneas</span>
                          <span className="font-bold text-white" data-id="element-5838">
                            {formatCurrency(lineItemsSubtotal)}
                          </span>
                        </div>
                        <div data-id="element-5839">
                          <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-5840">
                            Descuento ₡
                          </label>
                          <input type="number" min={0} value={discount} onChange={e => setDiscount(Number(e.target.value) || 0)} placeholder="0" className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5841" />
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t border-white/10" data-id="element-5842">
                          <span className="text-white font-semibold" data-id="element-5843">
                            Total
                          </span>
                          <span className="font-bold text-[#C9A34E]" data-id="element-5844">
                            {formatCurrency(effectiveTotal)}
                          </span>
                        </div>
                      </div>
                    </div>}
                </div>

                {/* ─── Existing Pago section ──────────────────── */}
                <div className="space-y-4" data-id="element-5845">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-5846">
                    Pago
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-5847">
                    <div data-id="element-5848">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5849">
                        Monto Total (Contrato)
                        {hasLineItems && <span className="ml-1 text-[10px] text-white/40" data-id="element-5850">
                            (auto)
                          </span>}
                      </label>
                      <div className="relative" data-id="element-5851">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" data-id="element-5852">
                          ₡
                        </span>
                        <input type="number" value={formData.totalAmount} onChange={e => setFormData({
                        ...formData,
                        totalAmount: Number(e.target.value)
                      })} disabled={hasLineItems} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-5853" />
                      </div>
                    </div>
                    <div data-id="element-5854">
                      <label className="block text-xs font-medium text-[#C9A34E] mb-1.5" data-id="element-5855">
                        Monto de este Pago
                      </label>
                      <div className="relative" data-id="element-5856">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C9A34E]/60" data-id="element-5857">
                          ₡
                        </span>
                        <input type="number" value={formData.paymentAmount} onChange={e => setFormData({
                        ...formData,
                        paymentAmount: Number(e.target.value)
                      })} className="w-full bg-[#C9A34E]/10 border border-[#C9A34E]/30 rounded-lg pl-8 pr-3 py-2 text-[#C9A34E] font-bold text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5858" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-5859">
                    <div data-id="element-5860">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5861">
                        Tipo de Pago
                      </label>
                      <select value={formData.paymentType} onChange={e => setFormData({
                      ...formData,
                      paymentType: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5862">
                        {PAYMENT_TYPES.map(type => <option key={type} value={type} data-id="element-5863">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-5864">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5865">
                        Método de Pago
                      </label>
                      <select value={formData.paymentMethod} onChange={e => setFormData({
                      ...formData,
                      paymentMethod: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5866">
                        {PAYMENT_METHODS.map(method => <option key={method} value={method} data-id="element-5867">
                            {method}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-5868">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5869">
                      Referencia (Opcional)
                    </label>
                    <input type="text" value={formData.paymentReference} onChange={e => setFormData({
                    ...formData,
                    paymentReference: e.target.value
                  })} placeholder="Ej. Comprobante #123456" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-5870" />
                  </div>

                  <div data-id="element-5871">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-5872">
                      Notas Adicionales
                    </label>
                    <textarea value={formData.notes} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} rows={2} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-5873" />
                  </div>
                </div>
              </div>
            </div>

            {/* Saved Receipts Panel */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden" data-id="element-5874">
              <button onClick={() => setShowSavedList(!showSavedList)} className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors" data-id="element-5875">
                <h2 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-5876">
                  <List className="w-5 h-5 text-blue-400" data-id="element-5877" />
                  Recibos Guardados
                  <span className="bg-white/10 text-white/70 text-xs py-0.5 px-2 rounded-full ml-2" data-id="element-5878">
                    {savedReceipts.length}
                  </span>
                </h2>
                {showSavedList ? <ChevronUp className="w-5 h-5 text-white/50" data-id="element-5879" /> : <ChevronDown className="w-5 h-5 text-white/50" data-id="element-5880" />}
              </button>

              {showSavedList && <div className="border-t border-white/10 p-4 max-h-96 overflow-y-auto custom-scrollbar" data-id="element-5881">
                  {loadingReceipts ? <div className="flex items-center justify-center py-8" data-id="element-5882">
                      <Loader2 className="w-6 h-6 text-white/30 animate-spin" data-id="element-5883" />
                    </div> : savedReceipts.length === 0 ? <div className="text-center py-8 text-white/40 text-sm" data-id="element-5884">
                      No hay recibos guardados aún.
                    </div> : <div className="space-y-2" data-id="element-5885">
                      {savedReceipts.map(receipt => <button key={receipt.id} onClick={() => loadReceipt(receipt)} className="w-full text-left bg-[#0B0B0B] hover:bg-white/5 border border-white/5 rounded-xl p-4 transition-colors group flex items-center justify-between" data-id="element-5886">
                          <div data-id="element-5887">
                            <div className="flex items-center gap-2 mb-1" data-id="element-5888">
                              <span className="text-sm font-bold text-white" data-id="element-5889">
                                {receipt.receipt_number}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-white/10 text-white/70" data-id="element-5890">
                                {receipt.payment_type}
                              </span>
                            </div>
                            <p className="text-sm text-white/70 font-medium" data-id="element-5891">
                              {receipt.client_name}
                            </p>
                            <p className="text-xs text-white/40 mt-1" data-id="element-5892">
                              {new Date(receipt.receipt_date).toLocaleDateString('es-CR')}{' '}
                              • {receipt.service_type}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-5893">
                            <p className="text-sm font-bold text-[#C9A34E]" data-id="element-5894">
                              {formatCurrency(receipt.payment_amount)}
                            </p>
                            <p className="text-xs text-white/40 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-5895">
                              Ver recibo →
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>}
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-7 print:col-span-12" data-id="element-5896">
            <div className="sticky top-24 print:static" data-id="element-5897">
              <h2 className="text-sm font-semibold text-white/60 mb-4 uppercase tracking-wider no-print" data-id="element-5898">
                Vista Previa del Recibo
              </h2>

              {/* THE RECEIPT */}
              <div className="receipt-container bg-white text-black rounded-xl p-8 sm:p-12 shadow-2xl shadow-black/50 max-w-2xl mx-auto border border-white/20 print:shadow-none print:border-none print:p-0 print:max-w-full print:rounded-none" data-id="element-5899">
                {/* Header */}
                <div className="flex justify-between items-start mb-10" data-id="element-5900">
                  <div data-id="element-5901">
                    <div className="flex items-center gap-2 mb-2" data-id="element-5902">
                      <div className="w-10 h-10 bg-[#0B0B0B] rounded-lg flex items-center justify-center print-bg-gray" data-id="element-5903">
                        <span className="text-[#C9A34E] font-bold text-2xl leading-none" data-id="element-5904">
                          N
                        </span>
                      </div>
                      <span className="font-bold text-3xl tracking-tight text-[#0B0B0B] print-text-black" style={{
                      fontFamily: "'Playfair Display', serif"
                    }} data-id="element-5905">
                        NOIZA
                      </span>
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-5906">
                      Plataforma de Delivery Digital
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-5907">Costa Rica</p>
                    <p className="text-sm text-gray-500" data-id="element-5908">noizacr.com</p>
                  </div>
                  <div className="text-right" data-id="element-5909">
                    <h2 className="text-3xl font-black text-gray-200 mb-2 uppercase tracking-widest" data-id="element-5910">
                      Recibo
                    </h2>
                    <p className="text-sm font-bold text-gray-800" data-id="element-5911">
                      N° {formData.receiptNumber}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-5912">
                      Fecha:{' '}
                      {new Date(formData.date).toLocaleDateString('es-CR')}
                    </p>
                  </div>
                </div>

                {/* Client Info */}
                <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-100 print-bg-gray print-border-black" data-id="element-5913">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-5914">
                    Recibí de:
                  </h3>
                  <p className="text-lg font-bold text-gray-800" data-id="element-5915">
                    {formData.clientName || 'Nombre del Cliente'}
                  </p>
                  <div className="flex gap-6 mt-1 text-sm text-gray-600" data-id="element-5916">
                    {formData.clientId && <p data-id="element-5917">
                        <span className="font-semibold" data-id="element-5918">Cédula:</span>{' '}
                        {formData.clientId}
                      </p>}
                    {formData.clientPhone && <p data-id="element-5919">
                        <span className="font-semibold" data-id="element-5920">Tel/WA:</span>{' '}
                        {formData.clientPhone}
                      </p>}
                  </div>
                </div>

                {/* Service Details — line items table or legacy */}
                <div className="mb-8" data-id="element-5921">
                  <table className="w-full text-left border-collapse" data-id="element-5922">
                    <thead data-id="element-5923">
                      <tr className="border-b-2 border-gray-200" data-id="element-5924">
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-5925">
                          Descripción
                        </th>
                        {hasLineItems && <>
                            <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-center w-16" data-id="element-5926">
                              Cant.
                            </th>
                            <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right w-28" data-id="element-5927">
                              Precio
                            </th>
                          </>}
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right w-32" data-id="element-5928">
                          {hasLineItems ? 'Subtotal' : 'Total Contrato'}
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-5929">
                      {hasLineItems ? <>
                          {/* Header row with service type as group title */}
                          <tr className="border-b border-gray-100" data-id="element-5930">
                            <td colSpan={4} className="py-3" data-id="element-5931">
                              <p className="font-bold text-gray-800" data-id="element-5932">
                                {formData.serviceType}
                              </p>
                              {formData.serviceDescription && <p className="text-sm text-gray-500 mt-1" data-id="element-5933">
                                  {formData.serviceDescription}
                                </p>}
                            </td>
                          </tr>
                          {lineItems.map(it => {
                        const subtotal = (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
                        return <tr key={it.id} className="border-b border-gray-100" data-id="element-5934">
                                <td className="py-3 pr-4 text-sm text-gray-700" data-id="element-5935">
                                  {it.description || <span className="italic text-gray-400" data-id="element-5936">
                                      Sin descripción
                                    </span>}
                                </td>
                                <td className="py-3 text-center text-sm text-gray-700" data-id="element-5937">
                                  {it.quantity}
                                </td>
                                <td className="py-3 text-right text-sm text-gray-700" data-id="element-5938">
                                  {formatCurrency(it.unitPrice)}
                                </td>
                                <td className="py-3 text-right text-sm font-semibold text-gray-800" data-id="element-5939">
                                  {formatCurrency(subtotal)}
                                </td>
                              </tr>;
                      })}
                        </> : <tr className="border-b border-gray-100" data-id="element-5940">
                          <td className="py-4 pr-4" data-id="element-5941">
                            <p className="font-bold text-gray-800 mb-1" data-id="element-5942">
                              {formData.serviceType}
                            </p>
                            <p className="text-sm text-gray-500" data-id="element-5943">
                              {formData.serviceDescription}
                            </p>
                          </td>
                          <td className="py-4 text-right font-semibold text-gray-800 align-top" data-id="element-5944">
                            {formatCurrency(formData.totalAmount)}
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>

                {/* Payment Breakdown */}
                <div className="flex justify-end mb-10" data-id="element-5945">
                  <div className="w-full sm:w-1/2 space-y-3" data-id="element-5946">
                    {hasLineItems && <>
                        <div className="flex justify-between text-sm text-gray-600" data-id="element-5947">
                          <span data-id="element-5948">Subtotal:</span>
                          <span data-id="element-5949">{formatCurrency(lineItemsSubtotal)}</span>
                        </div>
                        {discount > 0 && <div className="flex justify-between text-sm text-red-600" data-id="element-5950">
                            <span data-id="element-5951">Descuento:</span>
                            <span data-id="element-5952">-{formatCurrency(discount)}</span>
                          </div>}
                      </>}
                    <div className="flex justify-between text-sm text-gray-600" data-id="element-5953">
                      <span data-id="element-5954">Total del Contrato:</span>
                      <span data-id="element-5955">{formatCurrency(effectiveTotal)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-[#0B0B0B] border-y border-gray-200 py-3 print-text-black" data-id="element-5956">
                      <span data-id="element-5957">Monto Pagado:</span>
                      <span data-id="element-5958">{formatCurrency(formData.paymentAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium text-gray-500" data-id="element-5959">
                      <span data-id="element-5960">Saldo Pendiente:</span>
                      <span data-id="element-5961">{formatCurrency(remainingBalance)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Info & Notes */}
                <div className="grid grid-cols-2 gap-8 mb-12" data-id="element-5962">
                  <div data-id="element-5963">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-5964">
                      Detalles del Pago
                    </h3>
                    <p className="text-sm text-gray-800" data-id="element-5965">
                      <span className="font-semibold" data-id="element-5966">Tipo:</span>{' '}
                      {formData.paymentType}
                    </p>
                    <p className="text-sm text-gray-800" data-id="element-5967">
                      <span className="font-semibold" data-id="element-5968">Método:</span>{' '}
                      {formData.paymentMethod}
                    </p>
                    {formData.paymentReference && <p className="text-sm text-gray-800" data-id="element-5969">
                        <span className="font-semibold" data-id="element-5970">Referencia:</span>{' '}
                        {formData.paymentReference}
                      </p>}
                  </div>
                  {formData.notes && <div data-id="element-5971">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-5972">
                        Notas
                      </h3>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap" data-id="element-5973">
                        {formData.notes}
                      </p>
                    </div>}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 pt-6 flex justify-between items-end relative" data-id="element-5974">
                  <div data-id="element-5975">
                    <p className="text-xs text-gray-400 font-medium" data-id="element-5976">
                      Este recibo es un comprobante de pago válido.
                    </p>
                    <p className="text-xs text-gray-400 font-medium mt-1" data-id="element-5977">
                      Generado por NOIZA - Plataforma de Delivery Digital
                    </p>
                  </div>

                  {/* Watermark/Stamp */}
                  <div className="absolute right-0 bottom-4 opacity-10 rotate-[-15deg] pointer-events-none" data-id="element-5978">
                    <div className="border-4 border-black rounded-lg px-6 py-2" data-id="element-5979">
                      <span className="text-4xl font-black tracking-widest uppercase" data-id="element-5980">
                        {formData.paymentAmount >= effectiveTotal ? 'Pagado' : 'Abono'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}